import {meta} from "./meta";

export const dmarcChecker = {
    meta: meta,
    lookup: {
        title: "Is Your Domain Protected From Email Spoofing? Check Instantly with DMARC Flow.",
        subTitle: "What is the risk of my Domain? - Free Instant DMARC Record Scan",
        form: {
            title: "Enter your domain to immediately see if you are susceptible to spoofing, phishing or deliverability issues.",
            checking: "Checking",
            check: "Check DMARC"
        }
    },
    pricing: {
        "title": "Pricing & Plans",
        "subTitle": "Choose the right option – from free DIY setup to full-service protection.",

        "free": {
            "title": "Free Generator",
            "description": "For those confident editing DNS and who don't need reports.",
            "tooltip": "For technically skilled users",
            "price": "Free",
            "cta": "Use for Free"
        },

        "setup": {
            "title": "Setup Help",
            "description": "A DMARC expert sets everything up for you – no ongoing tools or reports.",
            "tooltip": "One-time help – no ongoing support",
            "price": "$80 one-time",
            "cta": "Book Setup Help"
        },
        "full": {
            "title": "Full Protection & Support",
            "description": "Includes setup, live reporting, alerts, and ongoing support.",
            "tooltip": "Ideal for businesses that want complete protection",
            "price": "from $39/month",
            "cta": "Start Full Protection",
            "badge": "Most Popular Option"
        }
    },
    painPoint: {
        title: "Is Your Domain at Risk of Email Attacks?",
        body1: "<span class='fw-bold'>Run a Free Instant Check.</span> No sign-up. No tech skills. Just type your domain to see if hackers can spoof your email—and what to do about it.",
        title2: "Why This Matters (35s Video 👉)",
        body2: "If your domain isn't protected by DMARC, you're not just at risk - <span class='fw-bold'>you're already a target</span>. Phishing emails can be sent from your domain, even without your knowledge. <ul class=\"list-group list-group-flush mb-4 mt-4\"><li class=\"list-group-item border-0\">✅ Customers lose trust instantly when spoofed emails appear to come from you</li><li class=\"list-group-item border-0\">✅ No control over your name—anyone could be impersonating your brand</li><li class=\"list-group-item border-0\">✅ Inbox placement suffers, hurting even legit email delivery</li></ul>",
        title3: "DMARCFlow = Full Protection, Not Just a Scan",
        body3: "We don't just say <span class='fw-bold'>you have a problem</span>. <div>We walk you through the fix - with real people, step-by-step, until you’re secure.</div><ul class=\"list-group list-group-flush mb-4 mt-4\"><li class=\"list-group-item border-0\">🔹Clear diagnosis</li><li class=\"list-group-item border-0\">🔹Expert setup</li><li class=\"list-group-item border-0\">🔹Ongoing support</li></ul>",
        scanMyDomainNow: "Scan My Domain",
        bookACall: "Talk to an Expert"
    },
    dashboard: {
        title: "You Checked Your Domain. Here's What Happens Next.",
        body: "We turn that \"you might be exposed” report into a fully protected domain—without you needing to touch code or learn DNS.",
        subTitle: "DMARCFlow isn't just a record checker. It's your full-service security partner.",
        title2: "Why Businesses Trust DMARCFlow:",
        body2: [
            {
                title: "✅ Expert Setup, Done With You",
                body: "We don't hand you a dashboard and walk away. A real consultant walks you through setup, step by step, until it's done right."
            },
            {
                title: "✅ Full-Suite Email Security",
                body: "SPF. DKIM. DMARC. BIMI. Everything you need to stop spoofing and protect your domain's reputation—configured the right way."
            },
            {
                title: "✅ Live Threat Reports You Can Understand",
                body: "No jargon. No noise. Just clear visual insights on who's sending what, and what's being blocked."
            },
            {
                title: "✅ Flat, Transparent Pricing",
                body: "No hidden fees. No email-volume penalties. Just one clear plan that scales with you—not against you."
            },
            {
                title: "✅ Real Human Support",
                body: "Have a question? You talk to an expert. Not a bot. Not a forum. Just fast answers and real help.\n"
            }
        ]
    },
    whatIsDmarc: {
        title: "What Is DMARC?",
        body: "DMARC is an email security standard that helps protect your domain from phishing, spoofing, and other unauthorized use. It works by telling receiving mail servers how to handle messages that fail SPF and DKIM checks. With DMARC, you ensure only legitimate emails get delivered — while gaining powerful insights through detailed reports. It's a critical step to strengthen email security, protect your brand, and improve deliverability."
    },
    whyDmarc: {
        title: "Why Choose DMARCFlow for DMARC Lookups?",
        subTitle: "Your trusted solution for email authentication, monitoring, and protection.",
        cards: {
            one: {
                title: "Comprehensive Reporting & Analysis",
                li: ["Get actionable insights into your domain's email authentication", "Detect spoofing attempts and misconfigurations quickly", "Visual dashboards and detailed reports for full transparency"]
            },
            two: {
                title: "User-Friendly Interface",
                li: ["Clean, intuitive dashboard for all experience levels", "Easily manage, analyze, and configure your DMARC records", "Step-by-step setup guides and helpful prompts"]
            },
            three: {
                title: "Enhanced Email Security",
                li: ["Defend your domain from phishing and impersonation", "Ensure only verified sources can send emails on your behalf", "Receive alerts to stay ahead of threats"]
            },
            four: {
                title: "Advanced DNS Record Checking",
                li: ["Instantly validate SPF, DKIM, and DMARC records", "Identify misconfigurations that impact security", "Get expert recommendations for optimal setup"]
            },
            five: {
                title: "Tailored for Every Use Case",
                li: ["Tools built for MSPs, enterprises, and growing teams", "Scalable solutions for businesses of any size", "Regular feature updates to meet evolving security needs"]
            },
            six: {
                title: "Trusted by Thousands Worldwide",
                li: ["Trusted across industries from startups to global brands", "Recognized for dependable results and outstanding service"]
            },
        },
        ctaOne: "DMARC Check Now",
        features: {
            title: "Everything You Need to Protect Your Email Infrastructure",
            subTitle: "Explore a complete set of tools built to strengthen your email security and safeguard your domain",
            list: {
                one: {
                    title: "DMARC Record Checker",
                    description: "Instantly validate your domain's DMARC record to uncover weaknesses and improve email protection"
                },
                two: {
                    title: "DKIM Tools",
                    description: "Generate, test, and analyze DKIM records to ensure your messages are properly signed and verified."
                },
                three: {
                    title: "SPF Record Optimizer",
                    description: "Simplify and flatten complex SPF records to avoid DNS lookup limits and reduce authentication issues."
                },
                four: {
                    title: "BIMI Management",
                    description: "Strengthen brand visibility and trust by showcasing your verified logo directly in recipients' inboxes."
                },
                five: {
                    title: "Domain Risk Scanner",
                    description: "Scan your domain for threats and misconfigurations to keep your email infrastructure fully secured."
                },
            }
        },
        faq: {
            "title": "Frequently Asked Questions",
            "subTitle": "Everything you need to know about DMARC Flow",
            list: [
                {
                    "question": "What is DMARC?",
                    "answer": "DMARC (Domain-based Message Authentication, Reporting and Conformance) is an email authentication protocol that helps protect your domain from spoofing and phishing attacks."
                },
                {
                    "question": "Do I need technical knowledge to use DMARC Flow?",
                    "answer": "No. DMARC Flow is designed to be used by non-technical users. We handle DNS setup, analysis, and reporting so you can focus on your business."
                },
                {
                    "question": "How does AI help with DMARC monitoring?",
                    "answer": "Our AI engine detects anomalies, false positives, and unusual sending behavior faster than manual analysis—improving response time and security."
                },
                {
                    "question": "Is DMARC Flow GDPR-compliant?",
                    "answer": "Yes. DMARC Flow is fully GDPR-compliant and runs on secure European servers with strong data protection policies."
                },
                {
                    "question": "Can I see who is misusing my domain?",
                    "answer": "Yes. DMARC Flow visualizes all unauthorized senders and helps you identify misuse attempts in real-time."
                },
                {
                    "question": "What are SPF and DKIM, and how do they relate to DMARC?",
                    "answer": "SPF and DKIM are authentication methods. DMARC builds on them to define how to handle suspicious emails and provides feedback via reports."
                },
                {
                    "question": "Can DMARC Flow help me improve email deliverability?",
                    "answer": "Yes. By using DMARC Flow, your domain becomes more trustworthy, which increases the likelihood that your emails reach the inbox."
                },
                {
                    "question": "How long does it take to get started?",
                    "answer": "Setup takes less than 5 minutes. Just add a DNS record and you're good to go. We'll guide you step-by-step."
                },
                {
                    "question": "What happens if I do nothing with DMARC?",
                    "answer": "Without DMARC, anyone can send emails pretending to be you. This can damage your reputation, cause delivery issues, and open legal risks."
                },
                {
                    "question": "Is there a free trial?",
                    "answer": "We have personal plans. You can test DMARC Flow without risk and see how it protects your domain from day one."
                }
            ]
        }
    }
};